import React, { ChangeEvent, InputHTMLAttributes, MouseEvent, useState } from "react";
import { useUpdateEffect } from "ahooks";
import { Product } from "@core/types";
import { useProductCardActions } from "../../../../../providers/useProductActions";
import { useConfig } from "../../../../../providers/config";
import { isWeightBasedProduct } from "../../../../../utils/productQuantity";

interface IQuantityInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "value" | "defaultValue" | "type" | "inputMode" | "onChange" | "onBlur"
  > {
  product: Product;
  cartQuantity: number;
  autoFit?: boolean;
}

const QUANTITY_PATTERN = /^\d*(?:\.\d{0,2})?$/;

const formatQuantity = (quantity: number) =>
  Number(quantity.toFixed(2)).toString().replace(".", ",");

const normalizeQuantity = (value: string) =>
  value.replace(/\s/g, "").replace(/,/g, ".");

export const QuantityInput = ({
  product,
  cartQuantity,
  autoFit = false,
  style,
  onClick,
  ...inputProps
}: IQuantityInputProps) => {
  const { setProductQuantity } = useProductCardActions();
  const { isWeightProductQuantityStepEnabled } = useConfig();

  const stockQuantity = product.stockQuantity || 0;
  const decimalInputAllowed =
    isWeightProductQuantityStepEnabled && isWeightBasedProduct(product);

  const [quantityText, setQuantityText] = useState(() =>
    cartQuantity > 0 ? formatQuantity(cartQuantity) : "",
  );

  useUpdateEffect(() => {
    setQuantityText(cartQuantity > 0 ? formatQuantity(cartQuantity) : "");
  }, [cartQuantity]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextText = event.target.value;
    const normalizedText = normalizeQuantity(nextText);

    if (!normalizedText) {
      setQuantityText("");
      return;
    }

    if (
      !QUANTITY_PATTERN.test(normalizedText) ||
      (!decimalInputAllowed && normalizedText.includes("."))
    ) {
      return;
    }

    setQuantityText(nextText);

    if (normalizedText.endsWith(".")) return;

    const quantity = Number(normalizedText);

    if (quantity <= 0) return;

    const nextQuantity = Math.min(quantity, stockQuantity);

    if (nextQuantity !== quantity) {
      setQuantityText(formatQuantity(nextQuantity));
    }

    setProductQuantity(product.id, nextQuantity, product);
  };

  const handleBlur = () => {
    if (!quantityText.trim()) {
      setProductQuantity(product.id, 0, product);
      return;
    }

    const quantity = Number(normalizeQuantity(quantityText));

    if (!Number.isFinite(quantity) || quantity <= 0) {
      setQuantityText("");
      setProductQuantity(product.id, 0, product);
      return;
    }

    const nextQuantity = Math.min(quantity, stockQuantity);

    setQuantityText(formatQuantity(nextQuantity));
    setProductQuantity(product.id, nextQuantity, product);
  };

  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onClick?.(event);
  };

  return (
    <input
      {...inputProps}
      type="text"
      inputMode={decimalInputAllowed ? "decimal" : "numeric"}
      dir="ltr"
      value={quantityText}
      onChange={handleChange}
      onBlur={handleBlur}
      onClick={handleClick}
      style={
        autoFit
          ? { ...style, width: `${Math.max(quantityText.length, 1)}ch` }
          : style
      }
    />
  );
};